import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Activity } from 'lucide-react'

interface ActivityItem {
  id: string
  module: string
  action: string
  title: string
  description?: string
  userName?: string
  createdAt: string
  tenderId?: string
  snagId?: string
  documentId?: string
  folderId?: string
}

interface ActivityTimelineProps {
  activities: ActivityItem[]
  projectId: string
}

const MODULE_DOT: Record<string, string> = {
  TENDER: 'bg-green-500',
  SNAG: 'bg-orange-500',
  DOCUMENT: 'bg-blue-500',
}

const MODULE_LABELS: Record<string, string> = {
  TENDER: 'Tender',
  SNAG: 'Snag',
  DOCUMENT: 'Document',
}

const formatRelativeTime = (dateStr: string) => {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const getItemLink = (item: ActivityItem, projectId: string): string | null => {
  if (item.tenderId) return `/project/${projectId}/tendering/tenders/${item.tenderId}`
  if (item.snagId) return `/project-setup/${projectId}/snags`
  if (item.documentId && item.folderId) return `/project/${projectId}/documents?folderId=${item.folderId}&documentId=${item.documentId}`
  if (item.documentId) return `/project/${projectId}/documents?documentId=${item.documentId}`
  if (item.folderId) return `/project/${projectId}/documents?folderId=${item.folderId}`
  return null
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ activities, projectId }) => {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">{t('Dashboard_activity_title', 'Recent Activity')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('Dashboard_no_activity', 'No recent activity')}
          </p>
        ) : (
          <div className="space-y-0">
            {activities.map((item, index) => {
              const link = getItemLink(item, projectId)
              const content = (
                <div className="flex items-center gap-3 py-2.5">
                  <div
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${MODULE_DOT[item.module] || 'bg-muted-foreground'}`}
                  />
                  <div className="flex-1 space-y-0.5 min-w-0">
                    <p className="text-sm leading-none">
                      <span className="font-medium">{item.action}</span>
                      {item.title && (
                        <span className="text-muted-foreground"> · {item.title}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {MODULE_LABELS[item.module] || item.module}
                      {item.userName && <span> · {item.userName}</span>}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatRelativeTime(item.createdAt)}
                  </span>
                </div>
              )

              return (
                <React.Fragment key={item.id}>
                  {link ? (
                    <Link to={link} className="block rounded-md -mx-2 px-2 hover:bg-muted/50 transition-colors">
                      {content}
                    </Link>
                  ) : (
                    content
                  )}
                  {index < activities.length - 1 && <Separator />}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default ActivityTimeline
